import React, { memo, useMemo } from 'react';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { resolveBulkEligibility } from '../../formReview.utils';
import {
  BULK_EXCLUSION_LABELS,
  FORM_REVIEW_LABELS,
  formatBulkAgreeCount,
  FormReviewTestIds,
} from '../../FormReviewCard.constants';
import {
  signOffActionsStyles,
  signOffCountsStyles,
  signOffSummaryStyles,
} from '../FormReviewSurface.styles';

const FormReviewBulkSummary = ({ form, isInFlight, onConfirm, onCancel }) => {
  const { eligible, excluded } = useMemo(() => resolveBulkEligibility(form), [form]);
  const hasEligible = eligible.length > 0;

  return (
    <Box data-testid={FormReviewTestIds.BULK_SUMMARY} sx={signOffSummaryStyles}>
      <Typography variant="bold12">{FORM_REVIEW_LABELS.BULK_TITLE}</Typography>
      <Box sx={signOffCountsStyles}>
        <Typography color="text.secondary" variant="regular12">
          {hasEligible
            ? formatBulkAgreeCount(eligible.length)
            : FORM_REVIEW_LABELS.BULK_NONE_ELIGIBLE}
        </Typography>
      </Box>
      {excluded.length > 0 ? (
        <Box>
          <Typography color="text.secondary" variant="bold12">
            {FORM_REVIEW_LABELS.BULK_EXCLUDED_TITLE}
          </Typography>
          {excluded.map(item => (
            <Typography
              color="text.secondary"
              data-testid={FormReviewTestIds.BULK_EXCLUDED_ROW}
              key={item.fieldKey}
              variant="regular12"
            >
              {`${item.label} — ${BULK_EXCLUSION_LABELS[item.reason] ?? item.reason}`}
            </Typography>
          ))}
        </Box>
      ) : null}
      <Box sx={signOffActionsStyles}>
        <Button
          data-testid={FormReviewTestIds.BULK_CANCEL}
          disabled={isInFlight}
          size="small"
          variant="line"
          onClick={onCancel}
        >
          {FORM_REVIEW_LABELS.BULK_CANCEL}
        </Button>
        <Button
          data-testid={FormReviewTestIds.BULK_CONFIRM}
          disabled={isInFlight || !hasEligible}
          size="small"
          onClick={onConfirm}
        >
          {FORM_REVIEW_LABELS.BULK_CONFIRM}
        </Button>
        {isInFlight ? <CircularProgress size={14} thickness={4} /> : null}
      </Box>
    </Box>
  );
};

FormReviewBulkSummary.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isInFlight: PropTypes.bool,
  form: PropTypes.shape({
    schemaVersionMatches: PropTypes.bool,
    fields: PropTypes.array,
    tableDiffs: PropTypes.array,
  }),
};

export default memo(FormReviewBulkSummary);
